import type { IntelligentInputType } from '@/interfaces/intelligentInput';
import type { MetaInfo } from '@/interfaces/metaInfo';
export interface Flow extends MetaInfo {
  id: string;
  name: string;
  disabled: boolean;
  template_id: string | null;
  nodes: FlowNode[];
}

export enum NodeType {
  ADS = 'ads',
  IOTHUB = 'iothub',
  ON_CHANGE = 'on_change',
  OPC_PULL = 'opc:pull',
  OPC = 'opc',
  OPC_EVENT = 'opc:event',
  OEE = 'oee',
  FAKE = 'fake',
  DERBY = 'derby',
  FTP_SAM = 'ftp:sam',
  STATION_TIMES = 'station_times',
  RELATIVE_CALCULATION = 'relative_calculation',
  MQTT_INPUT = 'mqtt:input',
  MQTT_OUTPUT = 'mqtt:output',
  DATAMODLER = 'datamodler',
  MODBUS = 'modbus',
  NODE_RED = 'node_red',
  MERGE = 'merge',
  FILTER = 'filter',
}

export const DEPRECATED_NODE_TYPES: ReadonlySet<NodeType> = new Set([NodeType.FAKE]);

export interface TemplateNode {
  id: string;
  depends_on: string[];
  config: Config;
}

export interface FlowNode extends TemplateNode {
  machine_id: string;
}

export type Config =
  | AdsConfig
  | OnChangeConfig
  | IotHubConfig
  | OEEConfig
  | StationTimesConfig
  | FakeConfig
  | DerbyConfig
  | OPCConfig
  | OPCPullConfig
  | OPCEventConfig
  | FTPSAMConfig
  | RelativeCalculationConfig
  | MqttInputConfig
  | MqttOutputConfig
  | DatamodlerConfig
  | ModbusConfig
  | MergeConfig
  | NodeRedConfig
  | FilterConfig;

export enum MergeStrategy {
  HASH = 'hash',
}

export interface MergeConfig {
  type: NodeType.MERGE;
  strategy: MergeStrategy;
  channel_ttl: number;
  merge_key: IntelligentInputType;
}

export interface DatamodlerConfig {
  type: NodeType.DATAMODLER;
  model_schema: string;
  mapping: Record<string, IntelligentInputType>;
}

export interface FTPSAMConfig {
  type: NodeType.FTP_SAM;
  url: IntelligentInputType;
  iothub_hostname: IntelligentInputType;
  custom_properties: Record<string, string>;
}

export interface MqttInputConfig {
  type: NodeType.MQTT_INPUT;
  url: IntelligentInputType;
  topic: IntelligentInputType;
  mapping: Record<string, IntelligentInputType>;
}

export interface MqttOutputConfig {
  type: NodeType.MQTT_OUTPUT;
  url: IntelligentInputType;
  topic: IntelligentInputType;
}

export interface AdsConfig {
  type: NodeType.ADS;
  uri: IntelligentInputType;
  interval: number;
  variables: AdsVariable[];
}

export interface OnChangeConfig {
  type: NodeType.ON_CHANGE;
  keep_alive: number;
  partial_message: boolean;
  logic_operation: string;
  rules: Rule[];
  default_rule: DefaultRule | null;
  keep_alive_immediate: boolean;
}

export enum Comparator {
  EQ = 'eq',
  NEQ = 'neq',
  GT = 'gt',
  LT = 'lt',
  GTE = 'gte',
  LTE = 'lte',
}

export enum LogicGate {
  AND = 'and',
  OR = 'or',
}

export interface FilterCondition {
  left: IntelligentInputType;
  comparator: Comparator;
  right: IntelligentInputType;
}

export interface FilterGroup {
  logic_gate: LogicGate;
  conditions: FilterCondition[];
}

export interface FilterConfig {
  type: NodeType.FILTER;
  logic_gate: LogicGate;
  groups: FilterGroup[];
}

export enum CloudFormatTypes {
  ADS = 'ads',
  OPC = 'opc',
  OEE = 'oee',
  DERBY = 'derby',
  DATAPOINT = 'datapoint',
  TIMESERIES = 'timeseries',
}

export interface IotHubConfig {
  type: NodeType.IOTHUB;
  iothub_hostname: IntelligentInputType;
  custom_properties: Record<string, string>;
  message_type: IntelligentInputType;
  service_info: ServiceInfo[];
  influx?: InfluxSettings | null;
  format: CloudFormatTypes;
  rate_limit: number;
}

export interface InfluxSettings {
  measurement: IntelligentInputType;
  tags: string[];
  static_tags?: Record<string, string>;
}

export enum PackMLVersion {
  V2 = 2,
  V3 = 3,
}

export interface OEEConfig {
  type: NodeType.OEE;

  pack_ml_version: PackMLVersion;

  state: IntelligentInputType;
  mode: IntelligentInputType;
  bad_packages: IntelligentInputType;
  good_packages: IntelligentInputType;
  actual_cycles: IntelligentInputType;
  target_cycles: IntelligentInputType;
}

export interface FakeConfig {
  type: NodeType.FAKE;
  interval: number;
  variables: Step[];
}

export interface DerbyConfig {
  type: NodeType.DERBY;
  pda_url: IntelligentInputType;
  audittrail_url: IntelligentInputType | null;
  websocket_bridge_url: IntelligentInputType;
  iothub_hostname: IntelligentInputType;
  custom_properties: Record<string, string>;
  output_enabled: boolean;
}

export interface RelativeCalculationConfig {
  type: NodeType.RELATIVE_CALCULATION;
  reset_on_negative: boolean;
  variables: Record<string, IntelligentInputType>;
}

interface OPCBaseConfig {
  url: IntelligentInputType;
  variables: OPCNode[];
  interval: number;
}

export interface OPCPullConfig extends OPCBaseConfig {
  type: NodeType.OPC_PULL;
}

export interface OPCConfig extends OPCBaseConfig {
  type: NodeType.OPC;
  partial_message: boolean;
}

export interface OpcEventField {
  name: string;
  alias: string;
  required: boolean;
}

export interface OPCEventConfig {
  type: NodeType.OPC_EVENT;
  url: IntelligentInputType;
  event: {
    node_id: string;
    node_type_id: string;
  };
  fields: OpcEventField[];
}

export interface ModbusConfig {
  type: NodeType.MODBUS;
  url: IntelligentInputType;
  interval: number;
  holding_register: RegisterConfig[];
}

export interface RegisterConfig {
  alias: string;
  address: number;
  datatype: ModbusDataType;
  register_count: number | null;
  swap_bytes: boolean;
  endian: 'big' | 'little';
}

export enum ModbusDataType {
  INT16 = 'INT16',
  UINT16 = 'UINT16',
  INT32 = 'INT32',
  UINT32 = 'UINT32',
  INT64 = 'INT64',
  UINT64 = 'UINT64',
  FLOAT32 = 'FLOAT32',
  FLOAT64 = 'FLOAT64',
  STRING = 'STRING',
  BITS = 'BITS',
}

interface NodeRedBaseNode {
  type: string | 'tab';
  label?: string;
}

export interface NodeRedConfig {
  type: NodeType.NODE_RED;
  url: IntelligentInputType;
  nodes: NodeRedBaseNode[];
}

export interface StationInfoVariable {
  station_id: string;
  instance: string;
  working_time: string;
  safety_time: string;
  good_packs: string;
  bad_packs: string;
}
export interface StationTimesConfig {
  type: NodeType.STATION_TIMES;
  cycles_variable: string;
  recipe_variable: string;
  station_variables: StationInfoVariable[];
  iothub_hostname: IntelligentInputType;
  custom_properties: Record<string, string>;
}

export interface AdsVariable {
  name: string;
  alias: string;
  required: boolean;
}

export enum StepMode {
  RANDOM_INT = 'random:int',
  RANDOM_FLOAT = 'random:float',
  LINEAR_INT = 'linear:int',
  LINEAR_FLOAT = 'linear:float',
  SINUSOIDAL = 'sinusoidal',
  CONSTANT = 'constant',
}

export interface Step {
  name: string;
  mode: StepMode;
  step_size: number;
  value: number;
}

export interface OPCNode {
  node_id: string;
  alias: string;
}

export interface DefaultRule {
  condition: Condition;
  value: string | number;
}

export interface Rule extends DefaultRule {
  variable: string;
}

export enum RuleLogic {
  AND = 'and',
  OR = 'or',
}

export enum Condition {
  EQ = 'eq',
  NEQ = 'neq',
  GT = 'gt',
  LT = 'lt',
  GTE = 'gte',
  LTE = 'lte',
  IN = 'in',
  NOT_IN = 'not_in',
  PRECISION = 'precision',
  PERCENTAGE = 'percentage',
}

export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export type ServiceInfo = Record<string, JsonValue>;

export interface NodeConfig {
  nodes: FlowNode[];
  version: string;
}
